import React from 'react';
import PropTypes from 'prop-types';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import {
    isEmpty,
    extractWorkSheetData,
    getWithExpiry,
    formatDate,
    ExcelDateToDateString
} from "../../../globals/GlobalFunctions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import axios from 'axios';
import {refreshToken} from "../../../globals/GlobalFunctions";
import Autocomplete from "@mui/material/Autocomplete";
import {GlobalConstants} from "../../../globals/GlobalConstants";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

export interface Props {
    excelData: any;
    currentDB: string;
}

function BulkClaimRepriceRemoveImporter({excelData, currentDB}: Props) {
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState(false);
    const [validationText, setValidationText] = React.useState("");
    const [validationError, setValidationError] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [currentClaimRepriceRemove, setCurrentClaimRepriceRemove] = React.useState({group_ID: "",
        effective_Date: "", division_ID: "", term_Date: "", ex_Code: "", vbasoftware_database: "CoastalAdminTest", vbapi_Token: "", apply_Network_ID: "",
        repriced_Network_ID: "", tax_ID: "", claimRepriceRemove_Key: 0});

    let token = localStorage.getItem('jwt');

    const handleValidationClose = () => {
        setValidationError(false);
        setValidationText("");
    }

    const initialRepriceRemoveState = {
        group_ID: "",
        effective_Date: "",
        division_ID: "",
        ex_Code: "",
        apply_Network_ID: "",
        vbasoftware_database: "CoastalAdminTest",
        vbapi_Token: "",
        repriced_Network_ID: "",
        term_Date: "",
        tax_ID: "",
        claimRepriceRemove_Key: 0
    }

    const clearClaimRepriceRemoves = () => {
        setCurrentClaimRepriceRemove({...initialRepriceRemoveState});
    }

    const importClaimRepriceRemoves = async (claimRepriceRemoveArray : any[]) => {
        refreshToken();
        setIsLoading(true);
        const response =
            await axios
                .put(
                    localStorage.getItem('baseURL') + 'claimRepriceRemove/putList',
                    claimRepriceRemoveArray,
                    {headers: {'Authorization': `Bearer ${token}`, accept: "*/*", "Content-Type": "application/json"}}
                ).then(() => {
                    setLoading(false);
                    setSuccessAlert(true);
                    setIsLoading(false);
                }).catch((error) => {
                    //console.log(response);
                    setLoading(false);
                    setValidationText(error.response.request.responseText);
                    setValidationError(true);
                    setIsLoading(false);
                });
        //console.log(response);
    }

    try {
        //Check for data and that there is a sheet in the data named IDCodeValue
        if (!isEmpty(excelData) && excelData.Sheets.ClaimRepriceRemove !== undefined) {
            //console.log(excelData, "Made it to formatter!");

            //global function to extract the data into a sequential array of cell data values as well as the column and row ranges
            let sheetDataArray = extractWorkSheetData(excelData.Sheets.ClaimRepriceRemove);

            let rawCellArray: any[] = sheetDataArray[0];

            let columnRange: number = sheetDataArray[1];

            let rowRange: number = sheetDataArray[2];

            let claimRepriceRemoveArray: any[] = [];

            //console.log(rawCellArray, "Raw Data");

            //we need to find the column index of the values we require so we can grab the data for each row -- THIS IS WHERE COLUMN NAMES ARE SET
            //we add 1 for each for the loop multiplier to account for it being zero based, but later we subtract 1 to account for the very fist row actually starting at zero
            const crrIndexCollection = {

                groupIndex: rawCellArray.indexOf('Group ID') + 1,
                divisionIndex: rawCellArray.indexOf('Division ID') + 1,
                exCodeIndex: rawCellArray.indexOf('Ex Code') + 1,
                repricedNetworkIndex: rawCellArray.indexOf('Repriced Net. ID') + 1,
                taxIDIndex: rawCellArray.indexOf('Prov. Tax ID') + 1,
                provNetworkIndex: rawCellArray.indexOf('Provider Network Relationship') + 1,
                effDateIndex: rawCellArray.indexOf('Eff. Date') + 1,
                termDateIndex: rawCellArray.indexOf('Term Date') + 1,
                keyIndex: rawCellArray.indexOf('Key') + 1,
            }

            if(crrIndexCollection.groupIndex > 0 && crrIndexCollection.taxIDIndex > 0) {

                for (let i = 1; i < rowRange; i++) {
                    //for each new array we skip over the previous records determined by the column length, removing 1 to account for our zero based index at the beginning
                    let rowMultiplier = columnRange * i - 1;

                    //start with group
                    if (crrIndexCollection.groupIndex > 0) {
                        currentClaimRepriceRemove.group_ID = rawCellArray[(rowMultiplier + crrIndexCollection.groupIndex)].toString();
                    }

                    //division
                    if (crrIndexCollection.divisionIndex > 0) {
                        currentClaimRepriceRemove.division_ID = rawCellArray[(rowMultiplier + crrIndexCollection.divisionIndex)].toString();
                    }

                    //key
                    if (crrIndexCollection.keyIndex > 0 && rawCellArray[(rowMultiplier + crrIndexCollection.keyIndex)].toString().length > 0) {
                        currentClaimRepriceRemove.claimRepriceRemove_Key = rawCellArray[(rowMultiplier + crrIndexCollection.keyIndex)].toString();
                    }

                    //ex code
                    if (crrIndexCollection.exCodeIndex > 0) {
                        currentClaimRepriceRemove.ex_Code = rawCellArray[(rowMultiplier + crrIndexCollection.exCodeIndex)].toString();
                    }

                    //repriced network
                    if (crrIndexCollection.repricedNetworkIndex > 0) {
                        currentClaimRepriceRemove.repriced_Network_ID = rawCellArray[(rowMultiplier + crrIndexCollection.repricedNetworkIndex)].toString();
                    }

                    //provider tax ID
                    if (crrIndexCollection.taxIDIndex > 0) {
                        currentClaimRepriceRemove.tax_ID = rawCellArray[(rowMultiplier + crrIndexCollection.taxIDIndex)].toString().toUpperCase();
                    }

                    //provider network relationship
                    if (crrIndexCollection.provNetworkIndex > 0) {
                        currentClaimRepriceRemove.apply_Network_ID = rawCellArray[(rowMultiplier + crrIndexCollection.provNetworkIndex)].toString().toUpperCase();
                    }

                    //effective Date
                    if (crrIndexCollection.effDateIndex > 0) {
                        let date = rawCellArray[(rowMultiplier + crrIndexCollection.effDateIndex)].toString();
                        if (date.includes("/")) {
                            date = formatDate(date);
                        } else if (date.length > 0 && date.length < 6) {
                            date = ExcelDateToDateString(date);
                        }
                        if (date.includes("-")) {
                            date = date.substring(5, 7) + "/" + date.substring(8, 10) + "/" + date.substring(0, 4);
                        }
                        else if (date.length > 7) {
                            date = date.substring(4, 6) + "/" + date.substring(6, 8) + "/" + date.substring(0, 4);
                        }
                        currentClaimRepriceRemove.effective_Date = date.toString().toUpperCase();
                    }

                    //term Date
                    if (crrIndexCollection.termDateIndex > 0) {
                        let date = rawCellArray[(rowMultiplier + crrIndexCollection.termDateIndex)].toString();
                        if (date.includes("/")) {
                            date = formatDate(date);
                        } else if (date.length > 0 && date.length < 6) {
                            date = ExcelDateToDateString(date);
                        }
                        if (date.includes("-")) {
                            date = date.substring(5, 7) + "/" + date.substring(8, 10) + "/" + date.substring(0, 4);
                        }
                        else if (date.length > 7) {
                            date = date.substring(4, 6) + "/" + date.substring(6, 8) + "/" + date.substring(0, 4);
                        }
                        currentClaimRepriceRemove.term_Date = date.toString().toUpperCase();
                    }

                    currentClaimRepriceRemove.vbasoftware_database = currentDB;

                    currentClaimRepriceRemove.vbapi_Token = getWithExpiry('vbapi_Token');

                    if(currentClaimRepriceRemove.group_ID.length > 0 && currentClaimRepriceRemove.tax_ID.length > 0
                        && currentClaimRepriceRemove.effective_Date.length > 0) {
                        claimRepriceRemoveArray.push({...currentClaimRepriceRemove});
                    }

                    currentClaimRepriceRemove.repriced_Network_ID = initialRepriceRemoveState.repriced_Network_ID;
                    currentClaimRepriceRemove.apply_Network_ID = initialRepriceRemoveState.apply_Network_ID;
                    currentClaimRepriceRemove.division_ID = initialRepriceRemoveState.division_ID;
                    currentClaimRepriceRemove.claimRepriceRemove_Key = initialRepriceRemoveState.claimRepriceRemove_Key;
                    currentClaimRepriceRemove.tax_ID = initialRepriceRemoveState.tax_ID;
                    currentClaimRepriceRemove.group_ID = initialRepriceRemoveState.group_ID;
                    currentClaimRepriceRemove.ex_Code = initialRepriceRemoveState.ex_Code;
                    currentClaimRepriceRemove.effective_Date = initialRepriceRemoveState.effective_Date;
                    currentClaimRepriceRemove.term_Date = initialRepriceRemoveState.term_Date;

                    clearClaimRepriceRemoves();
                }

                importClaimRepriceRemoves(claimRepriceRemoveArray);

                excelData.Sheets.ClaimRepriceRemove = undefined;
            }
            else {
                //setValidationText("Group ID, Plan ID, and Vendor are required.");
                //setValidationError(true);
            }

        }
    }
    catch(e) {
        console.log("ERROR", e);

        setErrorAlert(true);
        //clear it out after errors in case of rerendering otherwise the error spams will continue until morale improves
        excelData.Sheets.ClaimRepriceRemove = undefined;
    }

    return (
        <div>
            {successAlert ? <Alert className="groupAlertStyles" severity='success' onClose={() => setSuccessAlert(false)} variant="filled">
                <AlertTitle>Success!</AlertTitle>
                Claim Reprice Remove Import Complete!
            </Alert> : <></> }
            {errorAlert ? <Alert className="groupAlertStyles" severity='error' onClose={() => setErrorAlert(false)} variant="filled">
                <AlertTitle>Error!</AlertTitle>
                There was an error with this file!
            </Alert> : <></> }
            <Dialog
                open={validationError}
                onClose={handleValidationClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    {"Validation Error"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {validationText}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleValidationClose}>OK</Button>
                </DialogActions>
            </Dialog>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    );
}

export default React.memo(BulkClaimRepriceRemoveImporter)

BulkClaimRepriceRemoveImporter.propTypes = {
    excelData: PropTypes.object.isRequired
};
